<template class="">
    <section :class="token && isWheelActive ? 'bg-[#0b1220]' : ''">
        <section
            v-if="token && isWheelActive"
            class="relative overflow-hidden pb-8 bg-[#07142e] bg-[radial-gradient(60%_60%_at_15%_20%,#243b70_0%,transparent_60%),radial-gradient(60%_60%_at_85%_80%,#1d2a52_0%,transparent_60%)]"
        >
            <img :src="$asset('/hkcm/images/BackgoundLights.png', 'public')" aria-hidden="true" class="absolute top-0 left-0 z-0 w-full h-auto pointer-events-none opacity-50" />
            <WheelSparkles />

            <WheelOfFortuneDecoration class="hidden lg:block absolute -top-8 -right-12 pointer-events-none z-10" />
            <WheelOfFortuneDecoration class="hidden lg:block absolute -top-8 -left-12 pointer-events-none z-10 transform -scale-x-100" />

            <div class="relative z-[1] flex flex-col items-center pt-8 mb-4">
                <img :src="$asset('/hkcm/images/wheelTitle.png', 'public')" alt="Das HKCM-Glücksrad" class="w-auto max-w-[260px] sm:max-w-[340px] object-contain mb-2" />
                <h1 class="text-center text-white">Deine Chance: Dreh am Glücksrad!</h1>
            </div>
            <div class="relative z-[1]">
                <WheelOfFortune :token="token" />
            </div>
            <div v-if="wheel" class="relative z-[1] px-4 sm:px-6 mt-16 sm:mt-20 pb-4 max-w-[1224px] mx-auto">
                <WheelOfFurtuneCollapsibleContent :start-at="wheel?.start" :end-at="wheel?.end" :min-order-value="wheel?.minOrderValue" :landing-page="false" />
            </div>
        </section>

        <div v-else class="min-h-app container mx-auto py-16 flex flex-col justify-center">
            <div>
                <Info v-if="messages" v-model:data="messages" :closeable="true" />

                <div class="text-center">
                    <Icon type="Checkmark" viewBox="0 0 121 121" width="121" height="121" class="mx-auto fill-current text-blue" />

                    <h1 class="mt-8">
                        {{ $t('checkout_success_heading') }}
                    </h1>
                    <p class="mt-8">
                        {{ $t('checkout_success') }}
                    </p>
                    <p v-show="!loading && $store.getters['user/isLoggedIn'] && !$store.getters['user/isEmailVerified']">
                        {{ $t('not_confirmed_mail') }}
                        <span class="cursor-pointer underline" @click="requestVerifyEmail">{{ $t('resend_email') }}</span
                        >.
                    </p>
                    <FormButton class="mt-8 mx-auto" styling="primary" :value="$t('go_to_dashboard')" @click="$router.push('/profile/abonnements')" />
                </div>
            </div>
        </div>
    </section>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import api from '../../api';
import { getRegion } from '../../helpers.js';
import FormButton from '../form/FormButton.vue';
import WheelOfFortune from '../WheelOfFortune/WheelOfFortune.vue';
import WheelOfFortuneDecoration from '../WheelOfFortune/WheelOfFortuneDecoration.vue';
import WheelOfFurtuneCollapsibleContent from '../WheelOfFortune/WheelOfFurtuneCollapsibleContent.vue';
import WheelSparkles from '../WheelOfFortune/WheelSparkles.vue';

const route = useRoute();
const router = useRouter();
const token = computed(() => (route.query.token || '').toString());
const messages = ref(null);
const loading = ref(false);

const store = useStore();

const cart = computed(() => store.getters['cart/get']);
const wheel = computed(() => cart.value?.wheel || null);
const region = computed(() => getRegion());

const isWheelActive = computed(() => {
    if (region.value !== 'de' || wheel.value?.active !== true || !wheel.value?.start || !wheel.value?.end) return false;

    const now = new Date();
    const start = new Date(wheel.value.start);
    const end = new Date(wheel.value.end);

    return start <= now && now <= end;
});

onMounted(() => {
    if (window.meta?.messages) {
        messages.value = window.meta.messages;
    }

    const TTL_MS = 2 * 24 * 60 * 60 * 1000;

    const safeParse = (str) => {
        try {
            return JSON.parse(str);
        } catch {
            return null;
        }
    };

    const readPayload = () => {
        const ls = safeParse(localStorage.getItem('ga_purchase'));

        if (ls) return ls;

        const ss = safeParse(sessionStorage.getItem('ga_purchase'));

        return ss || null;
    };

    const cleanupPayload = () => {
        localStorage.removeItem('ga_purchase');
        sessionStorage.removeItem('ga_purchase');
    };

    const buildTransactionId = () => {
        const pad = (n) => String(n).padStart(2, '0');
        const d = new Date();
        const yyyy = d.getFullYear();
        const mm = pad(d.getMonth() + 1);
        const dd = pad(d.getDate());
        const hh = pad(d.getHours());
        const mi = pad(d.getMinutes());
        const ss = pad(d.getSeconds());
        const rnd = Math.floor(Math.random() * 900) + 100;

        return `HKCM_${yyyy}${mm}${dd}_${hh}${mi}${ss}_${rnd}`;
    };

    const pushPurchaseOnce = (stored) => {
        if (!stored) return;

        const savedAt = Number(stored?._savedAt || 0);

        if (savedAt && Date.now() - savedAt > TTL_MS) {
            cleanupPayload();

            return;
        }

        const txId = stored.transaction_id || buildTransactionId();
        const sentKey = `ga_purchase_sent_${txId}`;

        if (localStorage.getItem(sentKey) === '1') {
            cleanupPayload();

            return;
        }

        const { currency = 'EUR', value = 0, coupon = null, items = [], customer_data = {} } = stored;

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push({
            event: 'purchase',
            ecommerce: {
                transaction_id: txId,
                currency,
                value,
                ...(coupon ? { coupon } : {}),
                items
            },
            customer_data
        });

        twq('event', 'tw-oonqn-qmvss', {
            value,
            currency,
            contents: (Array.isArray(items) ? items : []).map((p) => ({
                content_type: p?.item_category || null,
                content_id: p?.item_id || null,
                content_name: p?.item_name || null,
                content_price: p?.price || null,
                num_items: p?.quantity || null,
                content_group_id: p?.item_brand || null
            })),
            conversion_id: txId
        });

        localStorage.setItem(sentKey, '1');
        cleanupPayload();
    };

    try {
        const payload = readPayload();
        pushPurchaseOnce(payload);
    } catch (e) {
        console.warn('[GA] purchase push failed:', e);
        cleanupPayload();
    }

    window.__gaPurchaseDebug = {
        pushNow: () => {
            const payload = safeParse(localStorage.getItem('ga_purchase')) || safeParse(sessionStorage.getItem('ga_purchase'));
            pushPurchaseOnce(payload);
        },
        clearAll: () => {
            cleanupPayload();
            Object.keys(localStorage).forEach((k) => {
                if (k.startsWith('ga_purchase_sent_')) localStorage.removeItem(k);
            });
            console.log('[GA] cleared payload & sent markers');
        }
    };
});

function requestVerifyEmail() {
    loading.value = true;
    api.post('/email/verification-notification').then(() => {
        router.push('/');
        loading.value = false;
    });
}
</script>

<style scoped>
.container {
    max-width: 900px;
    margin: 0 auto;
}
</style>
